import { Button } from '@components'
import { getSettingsPageUrl } from '@constants'
import { useBusinessContext } from '@context'
import { useUnpaidPlanStatus } from '@hooks'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { showFeaturebase } from '@utils'

interface BillingLockGuardProps {
  baseUrl: string
  children: React.ReactNode
}

export const BillingLockGuard: React.FC<BillingLockGuardProps> = ({ baseUrl, children }) => {
  const { data: business, slug } = useBusinessContext()
  const location = useLocation()

  if (business?.billing_lock_reason === 'manual') {
    return <ManualBillingLockScreen />
  }

  if (business?.billing_lock_reason === 'stripe_unpaid') {
    const normalizedPath = location.pathname.replace(/\/$/, '')
    const allowedPath = `${baseUrl}/settings`
    const page = new URLSearchParams(location.search).get('page')

    if (normalizedPath !== allowedPath || page !== 'plan-management') {
      return (
        <StripeUnpaidLockScreen
          planManagementUrl={getSettingsPageUrl(slug, { page: 'plan-management' })}
        />
      )
    }
  }

  return <>{children}</>
}

const StripeUnpaidLockScreen: React.FC<{ planManagementUrl: string }> = ({ planManagementUrl }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const { startPaymentMethodSetup, isStartingPaymentMethodSetup } = useUnpaidPlanStatus()

  return (
    <LockScreen>
      <StripeLockCard>
        <LockCardContent>
          <h1>{t('billingLock.stripeUnpaid.title')}</h1>
          <p>{t('billingLock.stripeUnpaid.description')}</p>
          <Actions>
            <Button
              type="button"
              intent="success"
              showSpinner={isStartingPaymentMethodSetup}
              disabled={isStartingPaymentMethodSetup}
              onClick={() => void startPaymentMethodSetup()}
            >
              {t('billingLock.stripeUnpaid.updatePaymentButton')}
            </Button>
            <Button type="button" onClick={() => history.push(planManagementUrl)}>
              {t('billingLock.stripeUnpaid.planManagementButton')}
            </Button>
            <Button type="button" isSecondary onClick={() => showFeaturebase('messages')}>
              {t('billingLock.contactSupportButton')}
            </Button>
          </Actions>
        </LockCardContent>
      </StripeLockCard>
    </LockScreen>
  )
}

const ManualBillingLockScreen: React.FC = () => {
  const [t] = useTranslation()
  const history = useHistory()
  return (
    <LockScreen>
      <LockCard>
        <LockCardContent>
          <h1>{t('billingLock.manual.title')}</h1>
          <p>
            <Trans i18nKey="billingLock.manual.description">
              <a href={t('link.email')} />
            </Trans>
          </p>
          <Actions>
            <Button type="button" intent="primary" onClick={() => showFeaturebase('messages')}>
              {t('billingLock.contactSupportButton')}
            </Button>
            <Button type="button" isSecondary onClick={() => history.push('/')}>
              {t('general.back')}
            </Button>
          </Actions>
        </LockCardContent>
      </LockCard>
    </LockScreen>
  )
}

const LockScreen = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
`

const LockCard = styled.div`
  max-width: 40rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxl}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  text-align: center;

  h1,
  p {
    margin: 0;
  }

  h1 {
    line-height: 1.3;
  }

  p {
    color: ${({ theme }) => theme.colors.metalGray};
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    line-height: 1.6;
    max-width: 32rem;

    a {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const StripeLockCard = styled(LockCard)`
  min-height: 16rem;
  justify-content: center;
`

const LockCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  width: 100%;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
`
